import React, { useContext, useEffect, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import DescriptorT from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import {
    TrackEvent,
    TrackEventRequest,
} from '@r6digital/rapidstor-types/react/entities/Tracking';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import { RapidStorSettings } from '@/utils/rapidstor';
import { fadeIn, fadeOut } from '@/utils/ui';
import { createId } from '@paralleldrive/cuid2';

import store from '../store';
import { setContactCard, setReservationData, setUnitData } from '../store/bookingSlice';

import Descriptor from '../components/descriptor';
import { FormType } from '../components/elements/contact-card';
import Header from '../components/elements/header';
import JourneyProgress from '../components/JourneyProgress';
import SizeSlider from '../components/SizeSlider';
import ReactSimplyCarousel from '../components/UnitListing/Carousel';
import { NavDirection } from '../components/UnitListing/Carousel';
import Alert from '@/components/elements/alert';

import { firstAvailableUnitType, isDescriptorAvailable } from '../features/rapidstor/helpers';
import { unitListingLoadedEvent } from '../features/rapidstor/PostMessageEvents';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import classes from '../scss/screens/unit-listing.module.scss';

import { usePostTrackEventMutation } from '../api/rapidstor';

export default function UnitListing() {
    /**
     * General page data
     */
    const location = useLoaderData() as Location;
    const locationData = useLocation();
    const navigate = useNavigate();
    const [cookies] = useCookies(['contactData']);
    const [personaCookie, setPersonaCookie] = useCookies(['persona']);
    const [searchParams, setSearchParams] = useSearchParams();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { 
        persona,
        unitList,
        unitListSuccess,
        unitListLoading,
        setPersona,
        descriptorList,
        unavailableDescriptorIds,
    } = useContext(JourneyContext);
    const { t } = useTranslation();
    const startPosition =
        `${window.rapidstor_settings.rapidstor_options.startPosition}` || searchParams.get('startPosition') || '0';
    const personaId = searchParams.get('personaId') || window.rapidstor_settings.rapidstor_options.persona || persona || personaCookie.persona || undefined;

    useEffect(() => {
        if (personaId && personaId !== persona) {
            setPersona(personaId);
            searchParams.delete('personaId');
            setSearchParams(searchParams);
            setPersonaCookie('persona', personaId, {
                path: '/',
                maxAge: 60 * 60 * 24 * 7,
            });
        }
    }, [personaId, persona, searchParams, setPersona, setPersonaCookie, setSearchParams]);

    /**
     * UI and Unit state
     */
    const [sizes, setSizes] = useState<number[]>([]);
    const [defaultSelected, setDefaultSelected] = useState<number>(0);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [descriptor, setDescriptor] = useState<DescriptorT>();
    const [carouselDirection, setCarouselDirection] = useState<NavDirection>('forward');

    const [postTrackEvent] = usePostTrackEventMutation();

    const [timesMoved, setTimesMoved] = useState<number>(0);

    const carouselOnMove = (newIndex: number) => {
        if (selectedIndex > newIndex) {
            setSelectedIndex(newIndex);
            setTimesMoved(timesMoved + 1);
            setCarouselDirection('backward');
        } else {
            setSelectedIndex(newIndex);
            setTimesMoved(timesMoved + 1);
            setCarouselDirection('forward');
        }
    };

    /**
     * Uses the descriptorList to get the area of each unit and sorts them ascending
     * @param units Descriptor[]
     * @returns number[]
     */
    const getUnitSizes = (units: DescriptorT[]) => {
        const areaArr: number[] = [];
        units.map((unit) => {
            areaArr.push(unit.area);
        });
        return areaArr.sort((a, b) => {
            return parseFloat(a.toString()) - parseFloat(b.toString());
        });
    };

    /**
     * Fires the unitListingLoadedEvent and the rapidstorTrackEvent after the unitList response is loaded
     */
    useEffect(() => {
        if (unitListSuccess && !unitListLoading) {
            unitListingLoadedEvent();
            const rapidStorTrackEventPayload: TrackEvent = {
                trackingId: createId(),
                url: window.location.href,
                referrerUrl: document.referrer,
                userAgent: window.navigator.userAgent,
                action: 'unit-listing',
                timestamp: new Date().toISOString(),
            };
            const rapidStorTrackEvent: TrackEventRequest = {
                locationToken: window.rapidstor_settings.rapidstor_options.locationToken,
                corpToken: window.rapidstor_settings.rapidstor_options.corpToken,
                payload: rapidStorTrackEventPayload,
            };
            postTrackEvent(rapidStorTrackEvent);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitListSuccess, unitListLoading]);

    useEffect(() => {
        fadeIn();
        if (!descriptorList.length) {
            setDescriptor(undefined);
            return;
        }

        if (descriptorList && sizes.length === 0) {
            const sortedList: DescriptorT[] = [];
            const sizes: (number | string)[] = [];
            descriptorList.map((d: DescriptorT, index: number) => {
                sizes.push(d.area);
                sortedList.push(descriptorList[index]);
            });
            setSizes(getUnitSizes(sortedList));
        }

        if (locationData.state?.unitUnavailableAtMoveIn && locationData.state?.unavailableDescriptor) {
            const unavailableUnitDescriptorIndex = descriptorList.findIndex((descriptor) => {
                return descriptor._id === locationData.state?.unavailableDescriptor;
            });

            if (unavailableUnitDescriptorIndex > -1) {
                setSelectedIndex(unavailableUnitDescriptorIndex);
                setDescriptor(descriptorList[unavailableUnitDescriptorIndex]);
                return;
            }
        }

        if (startPosition && parseInt(startPosition) < descriptorList.length) {
            setDefaultSelected(parseInt(startPosition));
            setSelectedIndex(parseInt(startPosition));
            setDescriptor(descriptorList[parseInt(startPosition)]);
        } else {
            setDefaultSelected(0);
            setSelectedIndex(0);
            setDescriptor(descriptorList[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [descriptorList, sizes.length, startPosition]);

    const toggleContactCard = (type: FormType, unitType?: UnitType) => {
        store.dispatch(
            setContactCard({
                animate: 'start',
                type,
                unitType: unitType ?? null,
                isDeal: !!unitType?.deal,
                deal: unitType?.deal?._id as string,
            })
        );
    };

    const goToBookingPage = async () => {
        const descriptor = descriptorList[selectedIndex];
        if (descriptor) {
            const unitType = firstAvailableUnitType(
                descriptorList[selectedIndex].unitTypes,
                location
            );
            store.dispatch(
                setUnitData({
                    location,
                    unitType,
                    descriptor,
                    descriptors: unitList?.descriptors ?? [],
                })
            );

            const showContactCardButton = (!isDescriptorAvailable(descriptor) || unavailableDescriptorIds.includes(descriptor._id))
                && !RapidStorSettings(location).alwaysAllowPricingPage()
                && RapidStorSettings(location).showSaveQuoteButton();

            if (showContactCardButton) {
                store.dispatch(setReservationData({
                    descriptor: descriptor,
                    unitType: unitType,
                }))
                toggleContactCard('contactMe', unitType);
                return;
            }

            firebaseLogEvent('unit_listing_carousel_scroll_count', {
                amount: `${timesMoved}`,
            });
            firebaseLogEvent('unit_listing_selected', {});

            if (location.rapidstor.hidePrices && !cookies.contactData) {
                store.dispatch(setReservationData({
                    descriptor: descriptor,
                    unitType: unitType,
                }))
                toggleContactCard('showPrices', unitType);
            } else {
                fadeOut();
                setTimeout(() => {
                    navigate(`/book-unit`);
                }, 400);
            }
        }
    };

    const journeyStepValue = useMemo(() => {
        return window.rapidstor_settings.app_config.pages.storageType ? 2 : 1;
    }, []);

    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                if (selectedIndex === 0) {
                    return;
                }
                setCarouselDirection('backward');
                setTimeout(() => {
                    setSelectedIndex(selectedIndex - 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            } else if (event.key === 'ArrowRight') {
                if (selectedIndex === descriptorList.length - 1) {
                    return;
                }
                setCarouselDirection('forward');
                setTimeout(() => {
                    setSelectedIndex(selectedIndex + 1);
                    setTimesMoved(timesMoved + 1);
                }, 50);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [selectedIndex, descriptorList, timesMoved]);

    return (
        <>
            <div style={{
                padding: '0 16px',
            }}>
                    <JourneyProgress stepsType='regular' mobile step={journeyStepValue} />
                    <>
                        <div className={classes.results_header}>
                            <div className={'d-flex'}>
                                <Header
                                    level={1}
                                    className={classes.title}
                                    data-testid='unitlisting-header'
                                >
                                    {t('unit_listing:header_1')}
                                    <span className='underline'>
                                        {t('unit_listing:header_2')}
                                    </span>
                                    {t('unit_listing:header_3')}
                                </Header>
                            </div>
                            <JourneyProgress
                                stepsType='regular'
                                desktop
                                step={journeyStepValue}
                            />
                            {descriptor && !unitListLoading && unitListSuccess ? (
                                <>
                                    { locationData.state?.unitUnavailableAtMoveIn && (
                                    <Alert
                                        type='warning'
                                        text={t('unit_listing:unit_unavailable_at_move_in')}
                                        fitContent={true}
                                    ></Alert>
                                    )}
                                    <div className={classes.mobile}>
                                        <Descriptor
                                            key={descriptorList[selectedIndex]._id}
                                            descriptor={descriptorList[selectedIndex]}
                                            onSelect={goToBookingPage}
                                            carousel={false}
                                            selectedIndex={selectedIndex}
                                            total={descriptorList.length}
                                            setSelectedIndex={carouselOnMove}
                                        />
                                    </div>
                                    <div className={`${classes.desktop}`}>
                                        <ReactSimplyCarousel
                                            activeSlideIndex={selectedIndex}
                                            onRequestChange={(index) => {
                                                setSelectedIndex(index);
                                                setTimesMoved(timesMoved + 1);
                                            }}
                                            sliderDirection={carouselDirection}
                                            containerProps={{
                                                style: {
                                                    width: '100%',
                                                    justifyContent: 'space-between',
                                                    userSelect: 'none',
                                                },
                                            }}
                                            preventScrollOnSwipe
                                            swipeTreshold={60}
                                            forwardBtnProps={{
                                                style: {
                                                    alignSelf: 'center',
                                                },
                                                disabled:
                                                    selectedIndex ===
                                                    descriptorList.length - 1,
                                            }}
                                            activeSlideProps={{
                                                style: {
                                                    opacity: 1,
                                                },
                                            }}
                                            backwardBtnProps={{
                                                style: {
                                                    alignSelf: 'center',
                                                },
                                                disabled: selectedIndex === 0,
                                            }}
                                            itemsToShow={1}
                                            speed={300}
                                        >
                                            {descriptorList.map(
                                                (
                                                    descriptor: DescriptorT,
                                                    index: number
                                                ) => (
                                                    <div
                                                        key={descriptor._id}
                                                        className={
                                                            classes.descriptor_wrapper
                                                        }
                                                    >
                                                        <Descriptor
                                                            descriptor={descriptor}
                                                            carousel={false}
                                                            active={
                                                                index === selectedIndex
                                                                    ? true
                                                                    : false
                                                            }
                                                            onSelect={goToBookingPage}
                                                        />
                                                    </div>
                                                )
                                            )}
                                        </ReactSimplyCarousel>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={`${classes.mobile}`}>
                                        <ContentLoader
                                            foregroundColor={`${location.skin.mainColour ?? '#eee'
                                                }`}
                                            backgroundColor='#f5f5f5'
                                            width={'100%'}
                                            height={'100vw'}
                                        >
                                            <rect
                                                x='0'
                                                y='4'
                                                rx='3'
                                                ry='3'
                                                width='100%'
                                                height='100%'
                                            />
                                        </ContentLoader>
                                    </div>

                                    <div className={`${classes.desktop}`}>
                                        <ContentLoader
                                            foregroundColor={`${location.skin.mainColour ?? '#eee'
                                                }`}
                                            backgroundColor='#f5f5f5'
                                            width={'100%'}
                                            height={'399px'}
                                        >
                                            <rect
                                                x='0'
                                                y='4'
                                                rx='3'
                                                ry='3'
                                                width='100%'
                                                height='100%'
                                            />
                                        </ContentLoader>
                                    </div>

                                    <ContentLoader
                                        foregroundColor={`${location.skin.mainColour ?? '#eee'
                                            }`}
                                        backgroundColor='#f5f5f5'
                                        width={'100%'}
                                        height={'60px'}
                                    >
                                        <rect
                                            x='0'
                                            y='24'
                                            rx='3'
                                            ry='3'
                                            width='100%'
                                            height='100%'
                                        />
                                    </ContentLoader>
                                </>
                            )}
                        </div>
                    </>
            </div>
            {descriptor && (
                <>
                    {location && descriptorList && defaultSelected !== undefined ? (
                        <div
                            style={{
                                paddingTop: '8px',
                                paddingLeft: '24px',
                                paddingRight: '24px',
                            }}
                        >
                            <SizeSlider
                                sizes={sizes}
                                activeStep={selectedIndex}
                                onSetActiveStep={carouselOnMove}
                            />
                        </div>
                    ) : null}
                </>
            )}
        </>
    );
}
